<template>
    <div class="page-container">
        <div class="grid-controls">
            <div class="grid-controls-row-1">
                <span :class="viewClass">Current view</span>
                <select
                    id="view-filter"
                    class="view-filter-select"
                    :class="viewSelectClass"
                    v-model="companyStore.currentView"
                    @change="viewSelectChanged()"
                >
                    <option
                        v-for="filter of Object.keys(ViewFilter)"
                        :value="ViewFilter[filter]"
                        :key="filter"
                    >
                        {{ getViewFilterText(ViewFilter[filter]) }}
                    </option>
                </select>
                <div class="search-controls">
                    <label for="match-algo-name" class="control-margin-r">Name search</label>
                    <select
                        id="match-algo-name"
                        class="match-algo-select"
                        v-model="companyStore.nameSearchType"
                        @change="refreshGrid()"
                    >
                        <option
                            v-for="type of Object.keys(CompanySearchType)"
                            :value="CompanySearchType[type]"
                            :key="type"
                        >
                            {{ type }}
                        </option>
                    </select>
                    <input
                        type="text"
                        v-model="companyStore.nameSearchTerm"
                        @input="refreshGrid()"
                        class="search-text control-margin-r"
                    >
                    <input type="button" value="Clear" @click="clearSearchName()" class="button button-sm">
                </div>
            </div>
            <div class="grid-controls-row-2">
                <div>
                    <input
                        type="button"
                        :disabled="disableButtons"
                        value="Create New Parent"
                        class="button control-margin-r"
                        @click="createParent()"
                    >
                    <input
                        type="button"
                        :disabled="disableButtons"
                        value="Add to Existing Parent"
                        class="button control-margin-r"
                        @click="existingGroup()"
                    >
                </div>
                 <div class="search-controls">
                    <label for="match-algo-addr" class="control-margin-r">Address search</label>
                    <select
                        id="match-algo-addr"
                        class="match-algo-select"
                        v-model="companyStore.addressSearchType"
                        @change="refreshGrid()"
                    >
                        <option
                            v-for="type of Object.keys(CompanySearchType)"
                            :value="CompanySearchType[type]"
                            :key="type"
                        >
                            {{ type }}
                        </option>
                    </select>
                    <input
                        type="text"
                        v-model="companyStore.addressSearchTerm"
                        @input="refreshGrid()"
                        class="search-text control-margin-r"
                    >
                    <input type="button" value="Clear" @click="clearSearchAddr()" class="button button-sm">
                </div>
            </div>
        </div>
        <div class="grid-record-count">
            <div class="record-count-text" :class="recordCountClass">{{ currentViewLabel }}: {{ recordCount }}</div>
        </div>
        <ag-grid-vue
            class="ag-theme-quartz company-grid"
            :class="viewClass"
            :rowData="companyRows"
            :columnDefs="colDefs"
            rowSelection="multiple"
            :rowMultiSelectWithClick="true"
            :suppressRowClickSelection="companyStore.currentView !== ViewFilter.Ungrouped"
            @grid-ready="onGridReady"
            @selectionChanged="rowSelected"
            @rowDoubleClicked="cellDoubleClick"
        />
    </div>
</template>

<script setup>
    import { ref, computed } from 'vue';
    import { AgGridVue } from 'ag-grid-vue3';
    import { promptModal } from "jenesius-vue-modal";
    import { AddressUtils } from '@/lib/AddressUtils';
    import { useCompanyStore, CompanySearchType, ViewFilter } from '@/stores/CompanyStore';
    import { useSettingsStore } from '@/stores/SettingsStore';
    import CreateParent from '@/components/CreateParent.vue';
    import ExistingGroup from '@/components/ExistingGroup.vue';
    import EditCompany from '@/components/EditCompany.vue';
    import Confirm from '@/components/Confirm.vue';
    import Error from '@/components/Error.vue';


    const companyStore = useCompanyStore();
    const settingsStore = useSettingsStore();
    let gridApi = null;
    const companyRows = ref(companyStore.companies);
    const recordCount = ref('');
    const disableButtons = ref(true);

    // Column Definitions: Defines & controls grid columns.
    const colDefs = ref([
        { field: 'selected', checkboxSelection: true, headerCheckboxSelection: true, width: 50 },
        { field: 'id', headerName: 'ID', width: 110, hide: !settingsStore.debugMode },
        { field: 'name', headerName: 'Name', width: 310 },
        { headerName: 'Address', valueGetter: p => AddressUtils.combineAddress(p.data), width: 430 },
        { headerName: 'Modified', valueGetter: p => p.data.hasOverride ? 'Yes' : '', width: 100 },
        {
            headerName: 'Division notes',
            flex: 1,
            autoHeight: true,
            cellRenderer: p => {
                const val = p.data.hasOverride ? p.data.overrideNotes || '' : p.data.description || '';
                return `<pre class="company-notes">${val}</pre>`;
            }
        }
    ]);

    async function onGridReady(params) {
        gridApi = params.api;
        await fetchCompanies();
    }

    async function fetchCompanies() {
        gridApi.showLoadingOverlay();

        try {
            await companyStore.fetchCompanies();
        }
        catch (err) {
            promptModal(Error, { message: 'Error retrieving division data', details: err.stack });
        }
        finally {
            gridApi.hideOverlay();
        }
        
        refreshGrid();
    }
``
    function clearSearchName() {
        companyStore.nameSearchTerm = '';
        refreshGrid();
    }

    function clearSearchAddr() {
        companyStore.addressSearchTerm = '';
        refreshGrid();
    }

    async function createParent() {
        const selected = gridApi.getSelectedNodes();
        const companies = selected.map(s => s.data);
        let moveNotesToParent = false;
        
        // if all the companies have identical notes (but they're not all null or empty string), give the user the
        // option to copy the note up into the parent and remove it from the all the divisions
        if (identicalCompanyNotes(companies) && (companies[0].description || '') !== '') {
            const res = await promptModal(
                Confirm,
                {
                    title: 'Consolidate Notes in Parent',
                    text: 'All selected companies have identical notes. Would you like to copy these notes up into ' +
                        'the parent company and remove them from the division companies?',
                    confirmBtnText: 'Yes Please',
                    cancelBtnText: 'No Thanks'
                }
            );

            if (res) {
                moveNotesToParent = true;
            }
        }

        const res = await promptModal(CreateParent, { companies, moveNotesToParent });

        if (res) {
            fetchCompanies();
        }
    }

    function identicalCompanyNotes(companies) {
        if (companies.length > 0) {
            const d0 = companies[0].description || '';

            if (d0 !== '') {
                for (let i = 1; i < companies.length; i++) {
                    const di = companies[i].description || '';

                    if (d0 !== di) {
                        return false;
                    }
                }
            }
        }

        return true;
    }

    async function existingGroup() {
        const selected = gridApi.getSelectedNodes();
        const res = await promptModal(ExistingGroup, { companies: selected.map(s => s.data) });
        console.log('promptModal res:', res);

        if (res) {
            fetchCompanies();
        }
    }

    function viewSelectChanged() {
        // only show row selection checkbox to user if viewing ungrouped companies -- we don't want them
        // attempting to group already grouped, hidden, or blocked companies
        gridApi.setColumnVisible('selected', companyStore.currentView === ViewFilter.Ungrouped);
        refreshGrid();
    }

    function refreshGrid() {
        gridApi.setGridOption('rowData', companyStore.companies);
        recordCount.value = companyStore.companies.length;
    }

    async function cellDoubleClick(event) {
        const save = await promptModal(EditCompany, { company: event.data });
        if (save) {
            fetchCompanies();
        }
    }

    function rowSelected(data) {
        const selected = gridApi.getSelectedNodes();
        disableButtons.value = selected.length === 0;
    }

    companyStore.$onAction(params => {
        if (params.name === 'updateCompanyNotes' || params.name === 'updateCompanyNotes2') {
            params.after(result => {
                // somebody changes a company note (probably in the edit company dialog)
                refreshGrid();
            });
        }
    });

    settingsStore.$onAction(params => {
        if (params.name === 'setDebugMode') {
            params.after(result => {
                gridApi.setColumnVisible('id', settingsStore.debugMode);
            });
        }
    })

    function getViewFilterText(value) {
        switch (value) {
            case ViewFilter.Ungrouped:
                return 'Ungrouped Divisions';

            case ViewFilter.Grouped:
                return 'Grouped Divisions';

            case ViewFilter.Hidden:
                return 'Hidden Divisions';
            
            case ViewFilter.Blocked:
                return 'Blocked Divisions';
        }
    }

    const currentViewLabel = computed(() => {
        switch (companyStore.currentView) {
            case ViewFilter.Ungrouped:
                return 'Ungrouped divisions';

            case ViewFilter.Grouped:
                return 'Grouped divisions';

            case ViewFilter.Hidden:
                return 'Hidden divisions';
            
            case ViewFilter.Blocked:
                return 'Divisions blocked from migration';
        }
    });

    const viewClass = computed(() => {
        switch (companyStore.currentView) {
            case ViewFilter.Ungrouped:
                return 'ungrouped-division-text';

            case ViewFilter.Grouped:
                return 'grouped-division-text';

            case ViewFilter.Hidden:
                return 'hidden-division-text';
            
            case ViewFilter.Blocked:
                return 'blocked-division-text';
        }
    });

    const viewSelectClass = computed(() => {
        switch (companyStore.currentView) {
            case ViewFilter.Ungrouped:
                return 'ungrouped-division-select';

            case ViewFilter.Grouped:
                return 'grouped-division-select';

            case ViewFilter.Hidden:
                return 'hidden-division-select';
            
            case ViewFilter.Blocked:
                return 'blocked-division-select';
        }
    });

    const recordCountClass = computed(() => {
        switch (companyStore.currentView) {
            case ViewFilter.Ungrouped:
                return 'record-count-text-ungrouped';

            case ViewFilter.Grouped:
                return 'record-count-text-grouped';

            case ViewFilter.Hidden:
                return 'record-count-text-hidden';
            
            case ViewFilter.Blocked:
                return 'record-count-text-blocked';
        }
    });
</script>

<style scoped>
    .page-container {
        display: flex;
        flex-direction: column;
        height: 100%;
    }
    
    .grid-controls {
        margin-top: 10px;
        margin-bottom: 10px;
    }

    .grid-controls-row-1 {
        display: flex;
        flex-direction: row;
        align-items: baseline;
    }

    .grid-controls-row-2 {
        display: flex;
        flex-direction: row;
        justify-content: left;
        align-items: baseline;
        margin-top: 15px;
    }

    .show-grouped-check {
        display: flex;
        flex-direction: row;
        justify-content: left;
        align-items: baseline;
        margin-right: 30px;
    }

    .show-grouped-check > input[type=checkbox] {
        margin-right: 8px;
    }
    .search-controls {
        flex: 3;
        display: flex;
        flex-direction: row;
        justify-content: right;
        align-items: baseline;
    }

    .match-algo-select {
        width: 100px;
        margin-right: 10px;
    }

    .search-text {
        flex-basis: 300px;
    }

    .control-margin-r {
        margin-right: 10px;
    }

    .company-grid {
        flex: 1
    }

    .grid-record-count {
        display: flex;
        flex-direction: row;
        justify-content: center;
        margin: 10px 0 15px 0;
    }

    .record-count-text {
        margin-bottom: 7px;
        padding: 7px 12px;
        border-radius: 16px;
        font-size: 12pt;
    }

    .record-count-text-ungrouped {
        color: white;
        background-color: #007bff;
    }

    .record-count-text-grouped {
        color: white;
        background-color: #11a69c;
    }

    .record-count-text-hidden {
        color: white;
        background-color: #a4a4a4;
    }

    .record-count-text-blocked {
        color: white;
        background-color: #cc0000;
    }

    .view-filter-select {
        margin-left: 10px;
        width: 200px;
    }

    .ungrouped-division-text {
        color: #007bff;
    }

    .grouped-division-text {
        color: #11a69c;
    }

    .hidden-division-text {
        color: #a4a4a4;
    }

    .blocked-division-text {
        color: #cc0000;
    }

    .ungrouped-division-select {
        color: #007bff;
        border-color: #007bff;
    }

    .grouped-division-select {
        color: #11a69c;
        border-color: #11a69c;
    }

    .hidden-division-select {
        color: #a4a4a4;
        border-color: #a4a4a4;
    }

    .blocked-division-select {
        color: #cc0000;
        border-color: #cc0000;
    }
</style>
